import { useEffect, useMemo, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { format, parseISO } from 'date-fns'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  milestoneSchema,
  type MilestoneFormValues,
} from '@/components/Calendars/calendarSchemas'
import {
  useCreateMilestone,
  useUpdateMilestone,
} from '@/hooks/useCalendarMilestones'
import { useMilestoneRules } from '@/hooks/useMilestoneRules'
import { resolveAllDates } from '@/utils/calendarDateResolver'
import type { CalendarMilestoneRead } from '@/types'

interface MilestoneFormProps {
  calendarId: string
  eventId: string
  milestone: CalendarMilestoneRead | null
  siblingMilestones: CalendarMilestoneRead[]
  allMilestones: CalendarMilestoneRead[]
  onSaved: () => void
  onCancel?: () => void
}

function humanizeConfigKey(key: string): string {
  return key
    .replace(/_pct$/, '')
    .replace(/_/g, ' ')
    .replace(/\b(id|dc|po)\b/gi, m => m.toUpperCase())
    .replace(/\b\w/g, c => c.toUpperCase())
}

function buildDefaultValues(
  ms: CalendarMilestoneRead | null
): MilestoneFormValues {
  if (!ms) {
    return {
      name: '',
      description: '',
      milestone_type: 'FREEFORM',
      date_mode: 'SPECIFIC',
      specific_date: '',
      fiscal_year: 0,
      fiscal_week: 0,
      calc_direction: undefined,
      calc_quantity: undefined,
      calc_unit: undefined,
      calc_from_milestone_id: '',
      rule_code: '',
      rule_configuration: {},
    }
  }

  return {
    name: ms.name,
    description: ms.description ?? '',
    milestone_type: ms.milestone_type,
    date_mode: ms.date_mode,
    specific_date: ms.specific_date ?? '',
    fiscal_year: ms.fiscal_year ?? 0,
    fiscal_week: ms.fiscal_week ?? 0,
    calc_direction: ms.calc_direction ?? undefined,
    calc_quantity: ms.calc_quantity ?? undefined,
    calc_unit: ms.calc_unit ?? undefined,
    calc_from_milestone_id: ms.calc_from_milestone_id ?? '',
    rule_code: ms.rule_code ?? '',
    rule_configuration: ms.rule_configuration ?? {},
  }
}

export function MilestoneForm({
  calendarId,
  eventId,
  milestone,
  siblingMilestones,
  allMilestones,
  onSaved,
  onCancel,
}: MilestoneFormProps) {
  const isEditMode = milestone !== null

  const [dateMethod, setDateMethod] = useState<'calendar' | 'fiscal'>(() => {
    if (
      milestone?.fiscal_year != null &&
      milestone?.fiscal_week != null &&
      !milestone?.specific_date
    ) {
      return 'fiscal'
    }
    return 'calendar'
  })

  const createMilestone = useCreateMilestone(calendarId, eventId)
  const updateMilestone = useUpdateMilestone(calendarId, eventId)
  const { data: milestoneRules } = useMilestoneRules()

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<MilestoneFormValues>({
    resolver: zodResolver(milestoneSchema),
    defaultValues: buildDefaultValues(milestone),
  })

  useEffect(() => {
    reset(buildDefaultValues(milestone))
    if (
      milestone?.fiscal_year != null &&
      milestone?.fiscal_week != null &&
      !milestone?.specific_date
    ) {
      setDateMethod('fiscal')
    } else {
      setDateMethod('calendar')
    }
  }, [milestone, reset])

  const watchMilestoneType = watch('milestone_type')
  const watchDateMode = watch('date_mode')
  const watchRuleCode = watch('rule_code')
  const watchSpecificDate = watch('specific_date')
  const watchCalcDirection = watch('calc_direction')
  const watchCalcQuantity = watch('calc_quantity')
  const watchCalcUnit = watch('calc_unit')
  const watchCalcFromId = watch('calc_from_milestone_id')

  const selectedRule = useMemo(() => {
    if (!milestoneRules || !watchRuleCode) return null
    return milestoneRules.find(r => r.code === watchRuleCode) ?? null
  }, [milestoneRules, watchRuleCode])

  const referenceMilestones = useMemo(
    () => siblingMilestones.filter(m => !milestone || m.id !== milestone.id),
    [siblingMilestones, milestone]
  )

  const resolvedDatePreview = useMemo(() => {
    if (watchDateMode === 'SPECIFIC') {
      if (watchSpecificDate) {
        try {
          return format(parseISO(watchSpecificDate), 'MMM d, yyyy')
        } catch {
          return null
        }
      }
      return null
    }

    if (
      watchDateMode === 'CALCULATED' &&
      watchCalcDirection &&
      watchCalcQuantity &&
      watchCalcUnit &&
      watchCalcFromId
    ) {
      const tempMilestone: CalendarMilestoneRead = {
        id: milestone?.id ?? '__temp__',
        event_id: eventId,
        name: '',
        description: null,
        owner: null,
        milestone_type: 'FREEFORM',
        display_order: 0,
        date_mode: 'CALCULATED',
        specific_date: null,
        fiscal_year: null,
        fiscal_week: null,
        calc_direction: watchCalcDirection,
        calc_quantity: watchCalcQuantity,
        calc_unit: watchCalcUnit,
        calc_from_milestone_id: watchCalcFromId,
        rule_code: null,
        rule_configuration: null,
        resolved_date: null,
        created_at: '',
        updated_at: '',
      }

      const milestonesForResolution = milestone
        ? allMilestones.map(m => (m.id === milestone.id ? tempMilestone : m))
        : [...allMilestones, tempMilestone]

      const resolved = resolveAllDates(milestonesForResolution)
      const dateStr = resolved.get(tempMilestone.id)
      if (dateStr) {
        try {
          return format(parseISO(dateStr), 'MMM d, yyyy')
        } catch {
          return null
        }
      }
    }

    return null
  }, [
    watchDateMode,
    watchSpecificDate,
    watchCalcDirection,
    watchCalcQuantity,
    watchCalcUnit,
    watchCalcFromId,
    milestone,
    allMilestones,
    eventId,
  ])

  const onSubmit = (values: MilestoneFormValues) => {
    const payload = {
      name: values.name,
      description: values.description || null,
      milestone_type: values.milestone_type,
      date_mode: values.date_mode,
      specific_date:
        values.date_mode === 'SPECIFIC' && values.specific_date
          ? values.specific_date
          : null,
      fiscal_year:
        values.date_mode === 'SPECIFIC' && values.fiscal_year
          ? values.fiscal_year
          : null,
      fiscal_week:
        values.date_mode === 'SPECIFIC' && values.fiscal_week
          ? values.fiscal_week
          : null,
      calc_direction:
        values.date_mode === 'CALCULATED'
          ? (values.calc_direction ?? null)
          : null,
      calc_quantity:
        values.date_mode === 'CALCULATED'
          ? (values.calc_quantity ?? null)
          : null,
      calc_unit:
        values.date_mode === 'CALCULATED' ? (values.calc_unit ?? null) : null,
      calc_from_milestone_id:
        values.date_mode === 'CALCULATED' && values.calc_from_milestone_id
          ? values.calc_from_milestone_id
          : null,
      rule_code:
        values.milestone_type === 'STANDARDIZED' && values.rule_code
          ? values.rule_code
          : null,
      rule_configuration:
        values.milestone_type === 'STANDARDIZED' && values.rule_configuration
          ? values.rule_configuration
          : null,
    }

    if (isEditMode) {
      updateMilestone.mutate(
        { msId: milestone.id, payload },
        { onSuccess: () => onSaved() }
      )
    } else {
      createMilestone.mutate(payload, { onSuccess: () => onSaved() })
    }
  }

  const isPending = createMilestone.isPending || updateMilestone.isPending

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="ms-name">Name</Label>
        <Input
          id="ms-name"
          {...register('name')}
          placeholder="Milestone name"
        />
        {errors.name && (
          <p className="text-sm text-destructive">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="ms-desc">Description</Label>
        <Textarea
          id="ms-desc"
          {...register('description')}
          placeholder="Optional description"
          rows={2}
        />
        {errors.description && (
          <p className="text-sm text-destructive">
            {errors.description.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Milestone Type
        </Label>
        <Controller
          name="milestone_type"
          control={control}
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="FREEFORM" id="type-freeform" />
                <Label
                  htmlFor="type-freeform"
                  className="cursor-pointer font-normal"
                >
                  Freeform
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="STANDARDIZED" id="type-standardized" />
                <Label
                  htmlFor="type-standardized"
                  className="cursor-pointer font-normal"
                >
                  Standardized
                </Label>
              </div>
            </RadioGroup>
          )}
        />
      </div>

      {watchMilestoneType === 'STANDARDIZED' && (
        <div className="space-y-3 rounded-md border p-3">
          <div className="space-y-2">
            <Label>Rule</Label>
            <Controller
              name="rule_code"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a rule" />
                  </SelectTrigger>
                  <SelectContent>
                    {milestoneRules?.map(rule => (
                      <SelectItem key={rule.code} value={rule.code}>
                        {rule.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.rule_code && (
              <p className="text-sm text-destructive">
                {errors.rule_code.message}
              </p>
            )}
          </div>

          {selectedRule &&
            Object.entries(selectedRule.config_schema).map(
              ([key, fieldDef]) => {
                const isNumeric = ['float', 'int', 'number'].includes(
                  fieldDef.type
                )
                const isPercentage = fieldDef.type === 'float'
                const rawValue =
                  (milestone?.rule_configuration?.[key] as number) ??
                  (fieldDef.default as number)
                const displayValue =
                  isPercentage && rawValue != null ? rawValue * 100 : rawValue

                return (
                  <div key={key} className="space-y-1">
                    <Label htmlFor={`rule-cfg-${key}`} className="text-sm">
                      {humanizeConfigKey(key)}
                      {isPercentage && ' (%)'}
                    </Label>
                    {fieldDef.description && !isPercentage && (
                      <p className="text-xs text-muted-foreground">
                        {fieldDef.description}
                      </p>
                    )}
                    <Input
                      id={`rule-cfg-${key}`}
                      type={isNumeric ? 'number' : 'text'}
                      defaultValue={displayValue ?? ''}
                      step={fieldDef.type === 'int' ? '1' : undefined}
                      onChange={e => {
                        const current = watch('rule_configuration') ?? {}
                        let value: unknown = e.target.value
                        if (isNumeric) {
                          const num = Number(e.target.value)
                          value = isPercentage ? num / 100 : num
                        }
                        setValue('rule_configuration', {
                          ...current,
                          [key]: value,
                        })
                      }}
                    />
                    {isPercentage && (
                      <p className="text-xs text-muted-foreground">
                        Enter as percentage (e.g. 100 for 100%)
                      </p>
                    )}
                  </div>
                )
              }
            )}
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Date Configuration
        </Label>
        <Controller
          name="date_mode"
          control={control}
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="SPECIFIC" id="mode-specific" />
                <Label
                  htmlFor="mode-specific"
                  className="cursor-pointer font-normal"
                >
                  Specific
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="CALCULATED" id="mode-calculated" />
                <Label
                  htmlFor="mode-calculated"
                  className="cursor-pointer font-normal"
                >
                  Calculated
                </Label>
              </div>
            </RadioGroup>
          )}
        />
      </div>

      {watchDateMode === 'SPECIFIC' && (
        <div className="space-y-3 rounded-md border p-3">
          <RadioGroup
            value={dateMethod}
            onValueChange={v => {
              const method = v as 'calendar' | 'fiscal'
              setDateMethod(method)
              if (method === 'calendar') {
                setValue('fiscal_year', 0)
                setValue('fiscal_week', 0)
              } else {
                setValue('specific_date', '')
                const now = new Date()
                const startOfYear = new Date(now.getFullYear(), 0, 1)
                const days = Math.floor(
                  (now.getTime() - startOfYear.getTime()) / 86400000
                )
                const currentWeek = Math.ceil(
                  (days + startOfYear.getDay() + 1) / 7
                )
                setValue('fiscal_year', now.getFullYear())
                setValue('fiscal_week', currentWeek)
              }
            }}
            className="flex gap-4"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="calendar" id="date-method-calendar" />
              <Label
                htmlFor="date-method-calendar"
                className="cursor-pointer font-normal text-sm"
              >
                Calendar Date
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="fiscal" id="date-method-fiscal" />
              <Label
                htmlFor="date-method-fiscal"
                className="cursor-pointer font-normal text-sm"
              >
                Fiscal Week
              </Label>
            </div>
          </RadioGroup>

          {dateMethod === 'calendar' ? (
            <div className="space-y-2">
              <Label htmlFor="ms-date">Date</Label>
              <Input id="ms-date" type="date" {...register('specific_date')} />
              {errors.specific_date && (
                <p className="text-sm text-destructive">
                  {errors.specific_date.message}
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="ms-fy" className="text-sm">
                  Fiscal Year
                </Label>
                <Input
                  id="ms-fy"
                  type="number"
                  {...register('fiscal_year')}
                  placeholder="e.g. 2026"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ms-fw" className="text-sm">
                  Fiscal Week
                </Label>
                <Input
                  id="ms-fw"
                  type="number"
                  min={0}
                  max={53}
                  {...register('fiscal_week')}
                  placeholder="0-53"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {watchDateMode === 'CALCULATED' && (
        <div className="space-y-3 rounded-md border p-3">
          <div className="space-y-2">
            <Label>Direction</Label>
            <Controller
              name="calc_direction"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select direction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ADD">Add</SelectItem>
                    <SelectItem value="SUBTRACT">Subtract</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.calc_direction && (
              <p className="text-sm text-destructive">
                {errors.calc_direction.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ms-qty" className="text-sm">
                Quantity
              </Label>
              <Input
                id="ms-qty"
                type="number"
                {...register('calc_quantity')}
                placeholder="e.g. 2"
              />
              {errors.calc_quantity && (
                <p className="text-sm text-destructive">
                  {errors.calc_quantity.message}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Unit</Label>
              <Controller
                name="calc_unit"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ?? ''}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DAY">Day</SelectItem>
                      <SelectItem value="WEEK">Week</SelectItem>
                      <SelectItem value="MONTH">Month</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.calc_unit && (
                <p className="text-sm text-destructive">
                  {errors.calc_unit.message}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>From Milestone</Label>
            <Controller
              name="calc_from_milestone_id"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select reference milestone" />
                  </SelectTrigger>
                  <SelectContent>
                    {referenceMilestones.map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.calc_from_milestone_id && (
              <p className="text-sm text-destructive">
                {errors.calc_from_milestone_id.message}
              </p>
            )}
          </div>
        </div>
      )}

      {resolvedDatePreview && (
        <div className="rounded-md bg-muted/50 p-3">
          <p className="text-sm font-medium">
            Resolved Date:{' '}
            <span className="text-primary">{resolvedDatePreview}</span>
          </p>
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button type="submit" disabled={isPending} className="cursor-pointer">
          {isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isEditMode ? (
            'Save'
          ) : (
            'Create'
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="cursor-pointer"
          onClick={() => {
            reset(buildDefaultValues(milestone))
            onCancel?.()
          }}
          disabled={isPending}
        >
          Cancel
        </Button>
      </div>
    </form>
  )
}
